"use client"

import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { EmployerBasicsAsync } from "./EmployerBasicsSlice"
import {
  BuildingOffice2Icon,
  MapPinIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline"
import { fetchProfileAsync } from "../../employerDashAPIs/companyProfile/CompanyProfileSlice"
import {
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "../../AboutYou/AboutSlice"

export function EmployerBasics() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Static list you provided (label kept as “Industry Type”)
  const industries = [
    { id: 1, uuid: "uuid1", title: "Information Technology" },
    { id: 2, uuid: "uuid2", title: "Finance" },
    { id: 3, uuid: "uuid3", title: "Healthcare" },
    { id: 4, uuid: "uuid4", title: "Education" },
    { id: 5, uuid: "uuid5", title: "Manufacturing" },
    { id: 6, uuid: "uuid6", title: "Retail" },
    { id: 7, uuid: "uuid7", title: "Telecommunications" },
    { id: 8, uuid: "uuid8", title: "Automotive" },
    { id: 9, uuid: "uuid9", title: "Construction" },
    { id: 10, uuid: "uuid10", title: "Marketing" },
    { id: 11, uuid: "uuid11", title: "Startup" },
    { id: 12, uuid: "uuid12", title: "MNC" },
    { id: 13, uuid: "uuid13", title: "Government" },
    { id: 14, uuid: "uuid14", title: "Private" },
    { id: 15, uuid: "uuid15", title: "Public Sector" },
    { id: 16, uuid: "uuid16", title: "Non-Profit" },
    { id: 17, uuid: "uuid17", title: "Educational Institution" },
    { id: 18, uuid: "uuid19", title: "Financial Institution" },
    { id: 19, uuid: "uuid20", title: "Consulting Firm" },
    { id: 20, uuid: "uuid21", title: "Product Based" },
    { id: 21, uuid: "uuid22", title: "Service Based" },
    { id: 22, uuid: "uuid23", title: "Freelance" },
    { id: 23, uuid: "uuid24", title: "Self-Employed" },
    { id: 24, uuid: "uuid25", title: "NGO" },
    { id: 25, uuid: "uuid26", title: "Research Organization" },
  ]

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
  })

  const countryList = useSelector(selectFetchCountry)
  const stateList = useSelector(selectFetchState)
  const citiesList = useSelector(selectFetchCities)

  const [selectedCountryId, setSelectedCountryId] = useState("")
  const [selectedStateId, setSelectedStateId] = useState("")
  const [filteredStates, setFilteredStates] = useState([])
  const [statesLoading, setStatesLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Prefer India UUID, fallback gracefully if not found
  const INDIA_UUID = "ecd3d725-a577-11ef-806e-89fb4f970b6e"

  // Fetch countries on mount
  useEffect(() => {
    if (!countryList || countryList.length === 0) {
      dispatch(fetchCountryAsync())
    }
  }, [dispatch, countryList])

  // Set default country (India if present, else first)
  useEffect(() => {
    if (!selectedCountryId && Array.isArray(countryList) && countryList.length > 0) {
      const india = countryList.find((c) => c.uuid === INDIA_UUID)
      const cid = india?.uuid || countryList[0]?.uuid || ""
      setSelectedCountryId(cid)
      setValue("country_uuid", cid)
    }
  }, [countryList, selectedCountryId, setValue])

  // Fetch states whenever country changes
  useEffect(() => {
    if (selectedCountryId) {
      setStatesLoading(true)
      dispatch(fetchStateAsync(selectedCountryId))
      // Reset dependent fields
      setSelectedStateId("")
      setValue("state_uuid", "")
      setValue("city_uuid", "")
    }
  }, [selectedCountryId, dispatch, setValue])

  // Build filteredStates robustly:
  // - If API returns states ONLY for selected country (common), just use stateList.
  // - If API returns all states with a country_uuid field, filter by country.
  useEffect(() => {
    const arr = Array.isArray(stateList) ? stateList : []
    let next = arr
    if (arr.length > 0) {
      const hasCountryField = arr.some((s) => Object.prototype.hasOwnProperty.call(s, "country_uuid"))
      next = hasCountryField ? arr.filter((s) => s.country_uuid === selectedCountryId) : arr
    }
    setFilteredStates(next)
    setStatesLoading(false)
  }, [stateList, selectedCountryId])

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedStateId) {
      dispatch(fetchCitiesAsync(selectedStateId))
      setValue("city_uuid", "")
    }
  }, [selectedStateId, dispatch, setValue])

  const onSubmit = async (details) => {
    setIsSubmitting(true)
    try {
      const formattedDetails = {
        ...details,
        pincode: Number(details.pincode),
      }
      const response = await dispatch(EmployerBasicsAsync(formattedDetails))
      if (response?.type === "EmployerBasics/add/fulfilled") {
        if (response?.payload?.msg === "Profile already exist!") {
          console.warn("Profile already exists:", response.payload.msg)
        } else {
          await dispatch(fetchProfileAsync())
          reset()
          navigate("/employer-dashboard")
        }
      }
    } catch (error) {
      console.error("Failed to create employer profile", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onSubmitError = (formErrors) => {
    const firstKey = Object.keys(formErrors)[0]
    if (!firstKey) return
    const el = document.getElementById(firstKey)
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      el.focus?.()
    }
  }

  const inputStyles =
    "h-12 px-4 w-full bg-white border border-gray-200 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ease-in-out placeholder-gray-400"
  const selectStyles =
    "h-12 px-4 w-full bg-white border border-gray-200 rounded-lg text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 ease-in-out appearance-none cursor-pointer pr-10"
  const labelStyles = "block text-sm font-medium text-gray-700 mb-1.5"

  const isCitiesDisabled = !selectedStateId

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-6 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-4">
          <BuildingOffice2Icon className="mx-auto h-12 w-12 text-indigo-600" />
          <h2 className="mt-4 text-3xl font-extrabold text-gray-900">Employer Registration</h2>
          <p className="mt-1 text-sm text-gray-600">Fill out some basic details and start hiring top talent.</p>
        </div>

        <div className="w-full bg-white/70 border border-indigo-100 rounded-xl p-3 mb-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-indigo-700">Step 1 of 2: Company Basics</span>
            <span className="text-xs text-gray-500">Next: Verify details</span>
          </div>
          <div className="mt-2 h-2 w-full bg-indigo-100 rounded-full overflow-hidden">
            <div className="h-2 bg-indigo-500 rounded-full transition-all" style={{ width: "50%" }} />
          </div>
        </div>

        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden">
          <div className="px-8 py-6 bg-gradient-to-r from-indigo-600 to-blue-500">
            <h3 className="text-xl font-semibold text-white">Company Information</h3>
            <p className="text-indigo-200">Let's get to know your company better</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit, onSubmitError)} className="p-8 space-y-2" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="company_name" className={labelStyles}>
                  Company's Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="company_name"
                  type="text"
                  autoFocus
                  {...register("company_name", {
                    required: "Company name is required",
                    minLength: { value: 2, message: "Please enter at least 2 characters" },
                    maxLength: { value: 100, message: "Maximum 100 characters allowed" },
                  })}
                  className={inputStyles}
                  placeholder="Enter company name"
                  aria-invalid={!!errors.company_name}
                />
                {errors.company_name && <p className="mt-1.5 text-sm text-red-600">{errors.company_name.message}</p>}
                <p className="mt-1 text-xs text-gray-500">Use the official registered name.</p>
              </div>

              <div>
                <label htmlFor="company_type" className={labelStyles}>
                  Industry Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="company_type"
                  {...register("company_type", { required: "Industry is required" })}
                  className={selectStyles}
                  defaultValue=""
                  aria-invalid={!!errors.company_type}
                >
                  <option value="" disabled>Select an industry</option>
                  {industries.map((item) => (
                    <option key={item.id} value={item.title}>
                      {item.title}
                    </option>
                  ))}
                </select>
                {errors.company_type && <p className="mt-1.5 text-sm text-red-600">{errors.company_type.message}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="number_of_employee" className={labelStyles}>
                Number of Employees <span className="text-red-500">*</span>
              </label>
              <select
                id="number_of_employee"
                {...register("number_of_employee", { required: "Number of employees is required" })}
                className={selectStyles}
                defaultValue=""
                aria-invalid={!!errors.number_of_employee}
              >
                <option value="" disabled>Select number of employees</option>
                <option value="1-50">1 to 50</option>
                <option value="50-100">50 to 100</option>
                <option value="100-500">100 to 500</option>
                <option value="500+">500+</option>
              </select>
              {errors.number_of_employee && <p className="mt-1.5 text-sm text-red-600">{errors.number_of_employee.message}</p>}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Country */}
              <div className="flex flex-col">
                <label htmlFor="country_uuid" className={labelStyles}>
                  Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="country_uuid"
                    className={selectStyles}
                    {...register("country_uuid", { required: "Country is required" })}
                    value={selectedCountryId}
                    onChange={(e) => {
                      const cid = e.target.value
                      setSelectedCountryId(cid)
                      setValue("country_uuid", cid)
                    }}
                    aria-invalid={!!errors.country_uuid}
                  >
                    {(countryList || []).map((item) => (
                      <option key={item.uuid || item.id} value={item.uuid}>
                        {item.name}
                      </option>
                    ))}
                  </select>

                </div>
                {errors.country_uuid && <p className="mt-1.5 text-sm text-red-600">{errors.country_uuid.message}</p>}
              </div>

              {/* State */}
              <div className="flex flex-col">
                <label htmlFor="state_uuid" className={labelStyles}>
                  State <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="state_uuid"
                    className={`${selectStyles} ${statesLoading ? "opacity-60" : ""}`}
                    {...register("state_uuid", { required: "State is required" })}
                    onChange={(e) => {
                      const sid = e.target.value
                      setSelectedStateId(sid)
                      setValue("state_uuid", sid)
                    }}
                    value={selectedStateId}
                    disabled={!selectedCountryId || statesLoading || (filteredStates || []).length === 0}
                    aria-invalid={!!errors.state_uuid}
                  >
                    <option value="" disabled>
                      {statesLoading ? "Loading states..." : "Select a state"}
                    </option>
                    {(filteredStates || []).map((state) => (
                      <option key={state.uuid || state.id} value={state.uuid}>
                        {state.name}
                      </option>
                    ))}
                  </select>

                </div>
                {errors.state_uuid && <p className="mt-1.5 text-sm text-red-600">{errors.state_uuid.message}</p>}
              </div>

              {/* City */}
              <div className="flex flex-col">
                <label htmlFor="city_uuid" className={labelStyles}>
                  City <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="city_uuid"
                    {...register("city_uuid", { required: "City is required" })}
                    className={`${selectStyles} ${isCitiesDisabled ? "opacity-60" : ""}`}
                    disabled={isCitiesDisabled}
                    aria-invalid={!!errors.city_uuid}
                  >
                    <option value="">{!selectedStateId ? "Select a state first" : "Select a city"}</option>
                    {Array.isArray(citiesList) &&
                      citiesList.length > 0 &&
                      citiesList.map((city) => (
                        <option key={city.uuid || city.id} value={city.uuid}>
                          {city.name}
                        </option>
                      ))}
                  </select>

                </div>
                {errors.city_uuid && <p className="mt-1.5 text-sm text-red-600">{errors.city_uuid.message}</p>}
              </div>

              {/* Pincode */}
              <div className="flex flex-col">
                <label htmlFor="pincode" className={labelStyles}>
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  id="pincode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 6)
                    e.target.value = v
                  }}
                  {...register("pincode", {
                    required: "Pincode is required",
                    pattern: { value: /^[0-9]{6}$/, message: "Please enter a valid 6-digit pincode" },
                  })}
                  className={inputStyles}
                  placeholder="Enter 6-digit pincode"
                  aria-invalid={!!errors.pincode}
                />
                {errors.pincode && <p className="mt-1.5 text-sm text-red-600">{errors.pincode.message}</p>}
                <p className="mt-1 text-xs text-gray-500">India pincode is 6 digits.</p>
              </div>
            </div>

            <div>
              <label htmlFor="address1" className={labelStyles}>
                Address 1 <span className="text-red-500">*</span>
              </label>
              <input
                id="address1"
                type="text"
                {...register("address1", {
                  required: "Address 1 is required",
                  minLength: { value: 5, message: "Please enter at least 5 characters" },
                })}
                className={inputStyles}
                placeholder="Building, street, area"
                aria-invalid={!!errors.address1}
              />
              {errors.address1 && <p className="mt-1.5 text-sm text-red-600">{errors.address1.message}</p>}
            </div>

            <div>
              <label htmlFor="address2" className={labelStyles}>
                Address 2
              </label>
              <input
                id="address2"
                type="text"
                {...register("address2")}
                className={inputStyles}
                placeholder="Landmark, locality (optional)"
              />
            </div>

            <div className="sticky bottom-0 bg-white pt-2">
              <button
                id="submit"
                type="submit"
                disabled={isSubmitting}
                className={`w-full flex justify-center items-center h-12 px-6 border border-transparent rounded-lg text-base font-medium text-white bg-indigo-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 ease-in-out ${isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
              <p className="mt-2 text-center text-xs text-gray-500">
                By continuing, you agree to our Terms and Privacy Policy.
              </p>
            </div>
          </form>
        </div>

        <div className="mt-3 text-center text-xs text-gray-500">
          Need help? Contact support or check our onboarding guide.
        </div>
      </div>
    </div>
  )
}